import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Brain, TrendingUp, TrendingDown, AlertTriangle, 
  ChevronRight, RefreshCcw, Info, Eye, Target,
  Clock, CheckCircle, AlertCircle, Activity,
  Zap, Shield, BarChart3
} from 'lucide-react';
import { formatAED } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Predictive Intelligence Admin Panel
 * Phase 1: Shadow Mode - Admin Only
 * 
 * AI-powered deal outcome predictions
 */
const PredictiveIntelligencePanel = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealPrediction, setDealPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch overview
      const overviewRes = await fetch(`${API_URL}/api/admin/predictions/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const overviewData = await overviewRes.json();
      if (overviewData.success) {
        setOverview(overviewData.data);
      }

      // Fetch pipeline
      const pipelineRes = await fetch(`${API_URL}/api/admin/predictions/pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pipelineData = await pipelineRes.json();
      if (pipelineData.success) {
        setPipeline(pipelineData.data);
      }
    } catch (err) {
      setError('Error connecting to Predictive Intelligence service');
    }
    setLoading(false);
  };

  const fetchDealPrediction = async (dealId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/predictions/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDealPrediction(data.data);
        setSelectedDeal(dealId);
      }
    } catch (err) {
      console.error('Error fetching prediction:', err);
    }
  };

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'LIKELY_SUCCESS': return 'bg-green-600/20 text-green-400 border-green-600/30';
      case 'MODERATE_RISK': return 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30';
      case 'HIGH_RISK': return 'bg-orange-600/20 text-orange-400 border-orange-600/30';
      case 'LIKELY_FAILURE': return 'bg-red-600/20 text-red-400 border-red-600/30';
      default: return 'bg-gray-600/20 text-gray-400 border-gray-600/30';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'LOW': return 'text-green-400';
      case 'MODERATE': return 'text-yellow-400';
      case 'HIGH': return 'text-orange-400';
      case 'CRITICAL': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getOutcomeLabel = (outcome) => {
    switch (outcome) {
      case 'LIKELY_SUCCESS': return 'Likely Success';
      case 'MODERATE_RISK': return 'Moderate Risk';
      case 'HIGH_RISK': return 'High Risk';
      case 'LIKELY_FAILURE': return 'Likely Failure';
      default: return outcome;
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 70) return 'text-green-400';
    if (prob >= 50) return 'text-yellow-400';
    if (prob >= 30) return 'text-orange-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-8 text-center">
          <Brain className="w-8 h-8 animate-pulse mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading Predictive Intelligence...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-red-600/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={fetchData} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display text-primary flex items-center gap-2">
            <Brain className="w-6 h-6" />
            Predictive Intelligence
          </h2>
          <p className="text-sm text-muted-foreground">Phase 1: Shadow Mode - AI Deal Outcome Predictions</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Shadow Mode Banner */}
      <Card className="glass-card border-violet-600/30 bg-violet-600/5">
        <CardContent className="p-4 flex items-center gap-4">
          <Zap className="w-5 h-5 text-violet-400" />
          <div>
            <p className="text-violet-400 font-medium">AI Predictions Active (Shadow Mode)</p>
            <p className="text-xs text-muted-foreground">
              Predicts completion probability, delay risk, and time-to-close using ARS, BSS, and TWM data.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {['overview', 'pipeline', 'simulate'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab)}
            className="capitalize"
          >
            {tab}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="glass-card border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-primary">{overview.total_active_deals}</div>
                <p className="text-xs text-muted-foreground">Active Deals</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-green-600/30">
              <CardContent className="p-4 text-center">
                <div className={`text-3xl font-display ${getProbabilityColor(overview.avg_completion_probability)}`}>
                  {overview.avg_completion_probability?.toFixed(0)}%
                </div>
                <p className="text-xs text-muted-foreground">Avg Completion Prob</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-yellow-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-yellow-400">
                  {overview.avg_delay_probability?.toFixed(0)}%
                </div>
                <p className="text-xs text-muted-foreground">Avg Delay Risk</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-violet-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-violet-400">
                  {overview.pipeline_health_score?.toFixed(0)}
                </div>
                <p className="text-xs text-muted-foreground">Pipeline Health</p>
              </CardContent>
            </Card>
          </div>

          {/* Outcome Distribution */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Outcome Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(overview.outcome_distribution || {}).map(([outcome, count]) => (
                  <div key={outcome} className={`p-4 rounded border ${getOutcomeColor(outcome)} text-center`}>
                    <div className="text-2xl font-display">{count}</div>
                    <p className="text-xs mt-1">{getOutcomeLabel(outcome)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Risk Distribution */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Risk Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {['LOW', 'MODERATE', 'HIGH', 'CRITICAL'].map((risk) => (
                  <div key={risk} className="p-4 rounded border border-white/10 text-center">
                    <div className={`text-2xl font-display ${getRiskColor(risk)}`}>
                      {overview.risk_distribution?.[risk] || 0}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{risk}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* At Risk Value */}
          {overview.high_risk_deals > 0 && (
            <Card className="glass-card border-red-600/30 bg-red-600/5">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  <div>
                    <p className="text-red-400 font-medium">{overview.high_risk_deals} High-Risk Deals</p>
                    <p className="text-xs text-muted-foreground">Requiring attention</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-display text-red-400">{formatAED(overview.at_risk_value)}</p>
                  <p className="text-xs text-muted-foreground">At-risk value</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Pipeline Tab */}
      {activeTab === 'pipeline' && pipeline && (
        <div className="grid grid-cols-3 gap-6">
          {/* Pipeline by Risk */}
          <Card className="glass-card border-white/10 col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Pipeline by Risk Level
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {['LOW', 'MODERATE', 'HIGH', 'CRITICAL'].map((risk) => (
                  <div key={risk} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${getRiskColor(risk)}`}>{risk} Risk</span>
                      <Badge variant="secondary">{pipeline.pipeline?.[risk]?.length || 0} deals</Badge>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {(pipeline.pipeline?.[risk] || []).map((deal) => (
                        <div
                          key={deal.deal_id}
                          onClick={() => fetchDealPrediction(deal.deal_id)}
                          className={`p-3 rounded border cursor-pointer transition-all ${
                            selectedDeal === deal.deal_id 
                              ? 'border-primary bg-primary/10' 
                              : 'border-white/10 hover:border-white/20'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">{formatAED(deal.value)}</p>
                              <p className="text-xs text-muted-foreground">{deal.deal_id?.slice(0, 8)}...</p>
                            </div>
                            <div className="text-right text-xs">
                              <p className={getProbabilityColor(deal.completion_probability)}>
                                {deal.completion_probability?.toFixed(0)}% completion
                              </p>
                              <p className="text-muted-foreground">{deal.time_to_close} days</p>
                            </div>
                          </div>
                          {deal.top_risk && (
                            <p className="text-xs text-orange-400 mt-1 truncate">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              {deal.top_risk}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Deal Prediction Detail */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                Deal Prediction
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dealPrediction ? (
                <div className="space-y-4">
                  {/* Main Probability */}
                  <div className="text-center p-4 rounded-lg bg-secondary/20">
                    <div className={`text-4xl font-display ${getProbabilityColor(dealPrediction.completion_probability)}`}>
                      {dealPrediction.completion_probability?.toFixed(0)}%
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Completion Probability</p>
                    <Badge className={`mt-2 ${getOutcomeColor(dealPrediction.outcome_prediction)}`}>
                      {getOutcomeLabel(dealPrediction.outcome_prediction)}
                    </Badge>
                  </div>

                  {/* Probabilities */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 rounded bg-yellow-600/10 text-center">
                      <div className="text-lg font-display text-yellow-400">
                        {dealPrediction.delay_probability?.toFixed(0)}%
                      </div>
                      <p className="text-xs text-muted-foreground">Delay Risk</p>
                    </div>
                    <div className="p-3 rounded bg-violet-600/10 text-center">
                      <div className="text-lg font-display text-violet-400">
                        {dealPrediction.amendment_probability?.toFixed(0)}%
                      </div>
                      <p className="text-xs text-muted-foreground">Amendment Risk</p>
                    </div>
                  </div>

                  {/* Time to Close */}
                  <div className="p-3 rounded bg-secondary/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="text-sm">Time to Close</span>
                    </div>
                    <div className="text-right">
                      <span className="font-display text-primary">{dealPrediction.estimated_time_to_close} days</span>
                      <p className="text-xs text-muted-foreground">
                        ({dealPrediction.time_range_min}-{dealPrediction.time_range_max} range)
                      </p>
                    </div>
                  </div>

                  {/* Risk Factors */}
                  {dealPrediction.risk_factors?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-red-400">Risk Factors</p>
                      <ul className="space-y-1">
                        {dealPrediction.risk_factors.slice(0, 3).map((factor, idx) => (
                          <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                            <AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                            <span>{factor.factor}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Success Factors */}
                  {dealPrediction.success_factors?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-green-400">Success Factors</p>
                      <ul className="space-y-1">
                        {dealPrediction.success_factors.slice(0, 3).map((factor, idx) => (
                          <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{factor.factor}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendations */}
                  {dealPrediction.recommendations?.length > 0 && (
                    <div className="p-3 rounded bg-primary/10 border border-primary/20">
                      <p className="text-sm font-medium text-primary mb-2">Recommendations</p>
                      <ul className="space-y-1">
                        {dealPrediction.recommendations.map((rec, idx) => (
                          <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                            <Target className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Confidence */}
                  <div className="text-xs text-muted-foreground text-center pt-2 border-t border-white/10">
                    <p>Confidence: <span className="text-primary">{dealPrediction.confidence_level}</span></p>
                    <p>Data Completeness: {dealPrediction.data_completeness?.toFixed(0)}%</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Brain className="w-8 h-8 mx-auto mb-4 opacity-50" />
                  <p>Select a deal to view prediction</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Stats */}
          <Card className="glass-card border-white/10 col-span-3">
            <CardContent className="p-4">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-display text-primary">{pipeline.summary?.total_deals}</p>
                  <p className="text-xs text-muted-foreground">Total Deals</p>
                </div>
                <div>
                  <p className="text-2xl font-display text-green-400">{pipeline.summary?.total_value_formatted}</p>
                  <p className="text-xs text-muted-foreground">Total Value</p>
                </div>
                <div>
                  <p className="text-2xl font-display text-red-400">{pipeline.summary?.at_risk_value_formatted}</p>
                  <p className="text-xs text-muted-foreground">At-Risk Value</p>
                </div>
                <div>
                  <p className="text-2xl font-display text-yellow-400">{pipeline.summary?.high_critical_count}</p>
                  <p className="text-xs text-muted-foreground">High/Critical Deals</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Simulate Tab */}
      {activeTab === 'simulate' && (
        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Prediction Simulator
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">
              Use the simulation API to test predictions with hypothetical parameters.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              GET /api/admin/predictions/simulate?bss_score=70&ars_score=70&financing_status=80
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PredictiveIntelligencePanel;
